<template>
  <validated-form
    name="address"
    :disabled="isSavingAddress || isProcessing"
    @valid-submit="submitAddress"
  >
    <div class="section section--half-padding">
      <div class="layout layout--horizontal">
        <h2 class="title title--3">
          {{ title }}
        </h2>

        <button
          v-if="currentUser.accountId && !showAddressForm"
          class="button button--small button--icon button--secondary"
          type="button"
          title="Add Address"
          @click="startAddingNewAddress"
        >
          <span class="icon far fa-plus" />
        </button>
      </div>

      <div
        v-if="isLoadingSavedAddresses"
        class="loader"
      />

      <template v-else>
        <div
          v-if="savedAddresses.length > 0"
          class="card-list card-list--clickable"
        >
          <label
            v-for="savedAddress in savedAddresses"
            :key="savedAddress.id"
            class="card-list__item"
            :for="`selectedAddress${ savedAddress.id}`"
          >
            <div class="card-list__item-section">
              <div class="checkbox-input checkbox-input--radio checkbox-input--default checkbox-input--no-label-text">
                <div class="checkbox-input__option">
                  <input
                    :id="`selectedAddress${ savedAddress.id}`"
                    class="checkbox-input__input"
                    type="radio"
                    name="selectedAddress"
                    :checked="selectedAddressId === savedAddress.id"
                    :value="savedAddress.id"
                    @input="selectAddress(savedAddress.id)"
                  >

                  <div class="checkbox-input__label" />
                </div>
              </div>
            </div>

            <div class="card-list__item-section card-list__item-section--stack grow">
              <p class="title title--6 dark no-spacing">
                {{ savedAddress.type }}
              </p>

              <p
                v-if="fullNameRequired && savedAddress.addressee"
                class="smaller no-spacing"
              >
                {{ savedAddress.addressee }}
              </p>

              <p class="smaller no-spacing">
                {{ savedAddress.line1 }},
                <template v-if="savedAddress.line2">
                  {{ savedAddress.line2 }},
                </template>
                <span class="no-wrap">
                  {{ savedAddress.city }},
                  {{ savedAddress.state }}
                  {{ savedAddress.postalCode }}
                </span>
              </p>
            </div>

            <div class="card-list__item-section">
              <button
                class="button button--icon button--small button--light button--light-text"
                title="Edit Saved Address"
                type="button"
                @click.prevent="selectAddressToEdit(savedAddress)"
              >
                <span class="icon far fa-pencil-alt" />
              </button>

              <button
                class="button button--icon button--small button--light button--danger"
                :class="{
                  'button--loading': deletingAddressId === savedAddress.id
                }"
                title="Delete Saved Address"
                type="button"
                @click.prevent="deleteAddress(savedAddress.id)"
              >
                <span class="icon far fa-trash-alt" />
              </button>
            </div>
          </label>
        </div>

        <div
          v-if="showAddressForm"
          :class="{
            'section': savedAddresses.length > 0,
            'section--alt-background': savedAddresses.length > 0,
            'section--bleed': savedAddresses.length > 0
          }"
        >
          <address-form
            v-model="address"
            :full-name-required="fullNameRequired"
          />

          <radio-input
            v-if="currentUser.accountId && !addressBeingEdited && validSaveAddressTypes.length > 0"
            v-model="saveAddressAsType"
            class="checkbox-input--inline"
            label="Save address as..."
            label-style="small"
            name="saveAddressAsType"
            :allow-uncheck="true"
            :options="validSaveAddressTypes"
          />
        </div>

        <div class="layout layout--horizontal layout--justify-end">
          <p
            v-if="!currentUser.accountId && merchant.options.signUpEnabled"
            class="smaller grow"
          >
            Have saved addresses?
            <button
              class="link primary"
              type="button"
              @click="$openModal('signIn')"
            >
              Sign in
            </button> to access them.
          </p>

          <button
            class="button button--primary button--large button--auto-width"
            :class="{
              'button--loading': isSavingAddress || isProcessing
            }"
            type="submit"
          >
            Use Address
          </button>
        </div>
      </template>
    </div>
  </validated-form>
</template>



<script>
  import errorHandlers from '@/services/error-handlers';
  import http from '@/services/http';
  import { mapGetters } from 'vuex';



  export default {
    name: 'DeliveryAddressForm',

    props: {
      fullNameRequired: {
        type: Boolean,
        default: false
      },
      isProcessing: {
        type: Boolean,
        default: false
      },
      title: {
        type: String,
        default: 'Delivery Address'
      }
    },

    data() {
      return {
        address: {
          addressee: null,
          city: null,
          countryCode: 'US',
          line1: null,
          line2: null,
          postalCode: null,
          state: null
        },
        addressBeingEdited: null,
        deletingAddressId: null,
        isLoadingSavedAddresses: false,
        isSavingAddress: false,
        saveAddressAsType: null,
        savedAddresses: [],
        selectedAddressId: null,
        showAddressForm: true
      };
    },

    computed: {
      validSaveAddressTypes() {
        const existingTypes = [];
        const validTypes = [];

        this.savedAddresses.forEach((address) => {
          existingTypes.push(address.type);
        });

        if (!existingTypes.includes('Home')) {
          validTypes.push({
            display: 'Home',
            value: 'Home'
          });
        }

        if (!existingTypes.includes('Work')) {
          validTypes.push({
            display: 'Work',
            value: 'Work'
          });
        }

        if (!existingTypes.includes('Other')) {
          validTypes.push({
            display: 'Other',
            value: 'Other'
          });
        }

        return validTypes;
      },

      ...mapGetters('session', ['currentUser', 'orderDetails']),
      ...mapGetters('merchant', ['merchant'])
    },

    watch: {
      'currentUser.accountId': 'loadSavedAddresses'
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        this.loadSavedAddresses();
      },

      async deleteAddress(id) {
        try {
          this.deletingAddressId = id;

          await http.delete(`/cardfree-api/v1/account/me/address/${id}`);

          this.savedAddresses = this.savedAddresses.filter(address => address.id !== id);

          if (id === this.selectedAddressId) {
            this.selectAddress(null);
          }
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.deletingAddressId = null;
        }
      },

      async loadSavedAddresses() {
        try {
          if (!this.currentUser.accountId) {
            this.selectDefaultAddress();

            return;
          }

          this.isLoadingSavedAddresses = true;

          const response = await http.get('/cardfree-api/v1/account/me/address');

          this.savedAddresses = response.data;

          this.selectDefaultAddress();
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isLoadingSavedAddresses = false;
        }
      },

      async saveAddress() {
        try {
          const body = {
            addressee: this.address.addressee,
            city: this.address.city,
            countryCode: this.address.countryCode,
            line1: this.address.line1,
            line2: this.address.line2,
            postalCode: this.address.postalCode,
            state: this.address.state,
            type: this.saveAddressAsType
          };

          if (this.saveAddressAsType) {
            const response = await http.post('/cardfree-api/v1/account/me/address', body);

            this.savedAddresses.push(response.data);
            this.selectAddress(response.data.id);
          }

          else if (this.addressBeingEdited) {
            body.type = this.addressBeingEdited.type;

            const response = await http.put(`/cardfree-api/v1/account/me/address/${this.addressBeingEdited.id}`, body);

            const udpatedAddress = response.data;

            this.savedAddresses.forEach((address) => {
              if (address.id === udpatedAddress.id) {
                Object.assign(address, udpatedAddress);
              }
            });

            this.selectAddress(udpatedAddress.id);
          }
        }

        catch (error) {
          throw error;
        }
      },

      selectAddress(id) {
        this.addressBeingEdited = null;
        this.saveAddressAsType = null;
        this.selectedAddressId = id;
        this.showAddressForm = !id;
      },

      selectAddressToEdit(address) {
        Object.assign(this.address, {
          addressee: address.addressee,
          city: address.city,
          countryCode: address.countryCode,
          line1: address.line1,
          line2: address.line2,
          postalCode: address.postalCode,
          state: address.state
        });

        this.selectAddress(address.id);

        this.addressBeingEdited = address;
        this.showAddressForm = true;
      },

      selectDefaultAddress() {
        if (this.orderDetails.deliveryAddress) {
          if (this.orderDetails.deliveryAddress.id) {
            const matchingAddress = this.savedAddresses.find(address => address.id === this.orderDetails.deliveryAddress.id);

            if (matchingAddress) {
              this.selectAddress(this.orderDetails.deliveryAddress.id);
              return;
            }
          }

          this.address.addressee = this.orderDetails.deliveryAddress.addressee;
          this.address.city = this.orderDetails.deliveryAddress.city;
          this.address.countryCode = this.orderDetails.deliveryAddress.countryCode;
          this.address.line1 = this.orderDetails.deliveryAddress.line1;
          this.address.line2 = this.orderDetails.deliveryAddress.line2;
          this.address.postalCode = this.orderDetails.deliveryAddress.postalCode;
          this.address.state = this.orderDetails.deliveryAddress.state;
        }

        else if (this.savedAddresses.length > 0) {
          this.selectAddress(this.savedAddresses[0].id);
        }
      },

      startAddingNewAddress() {
        this.selectAddress(null);
      },

      async submitAddress() {
        try {
          this.isSavingAddress = true;

          await this.saveAddress();

          this.updateSelectedValue();
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isSavingAddress = false;
        }
      },

      updateSelectedValue() {
        let { address } = this;

        if (this.selectedAddressId) {
          const savedAddress = this.savedAddresses.find(addr => addr.id === this.selectedAddressId);

          if (savedAddress) {
            address = savedAddress;
          }
        }

        this.$emit('selected', address);
      }
    }
  };
</script>
